import CryptoJS from "crypto-js";

import { STORAGE_KEYS } from "../constants";

const AES_KEY = process.env.REACT_APP_STORAGE_AES_KEY!;
const AES_IV = process.env.REACT_APP_STORAGE_AES_IV!;

if (!AES_KEY || !AES_IV) {
  throw new Error("[StorageService] Environment variables are not defined");
}

const CRYPTO = {
  key: CryptoJS.enc.Utf8.parse(AES_KEY),
  iv: CryptoJS.enc.Utf8.parse(AES_IV),
  cfg: { mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 },
} as const;

interface AuthTokens {
  access: { token: string; user_id: string };
  refresh: { token: string };
}

type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

const hashKey = (key: string): string =>
  CryptoJS.HmacSHA256(key, CRYPTO.key).toString(CryptoJS.enc.Base64);

const encrypt = (value: string): string =>
  CryptoJS.AES.encrypt(value, CRYPTO.key, {
    iv: CRYPTO.iv,
    mode: CRYPTO.cfg.mode,
    padding: CRYPTO.cfg.padding,
  }).toString();

const decrypt = (cipher: string): string | null => {
  try {
    const bytes = CryptoJS.AES.decrypt(cipher, CRYPTO.key, {
      iv: CRYPTO.iv,
      mode: CRYPTO.cfg.mode,
      padding: CRYPTO.cfg.padding,
    });
    return bytes.toString(CryptoJS.enc.Utf8) || null;
  } catch {
    return null;
  }
};

const _set = (key: string, value: unknown): void => {
  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(hashKey(key), encrypt(serialized));
  } catch (error) {
    console.error(`[StorageService] set failed: ${key}`, error);
  }
};

const _get = <T>(key: string): T | null => {
  try {
    const cipher = localStorage.getItem(hashKey(key));
    if (!cipher) return null;

    const decrypted = decrypt(cipher);
    if (!decrypted) return null;

    try {
      return JSON.parse(decrypted) as T;
    } catch {
      return decrypted as unknown as T;
    }
  } catch (error) {
    console.error(`[StorageService] get failed: ${key}`, error);
    return null;
  }
};

const _remove = (key: string): void => {
  try {
    localStorage.removeItem(hashKey(key));
  } catch (error) {
    console.error(`[StorageService] remove failed: ${key}`, error);
  }
};

export const storageService = {
  persistSession(
    udid: string,
    tokens: AuthTokens,
    isReg: boolean,
    isPurch: boolean,
  ): void {
    _set(STORAGE_KEYS.UDID, udid);
    _set(STORAGE_KEYS.ACCESS_TOKEN, tokens.access.token);
    _set(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh.token);
    _set(STORAGE_KEYS.USER_ID, tokens.access.user_id);
    _set(STORAGE_KEYS.IS_NUMBER_REG, isReg);
    _set(STORAGE_KEYS.IS_NUMBER_PURCHASED, isPurch);
  },

  getAccessToken: (): string | null => _get<string>(STORAGE_KEYS.ACCESS_TOKEN),
  setAccessToken: (token: string) => _set(STORAGE_KEYS.ACCESS_TOKEN, token),

  getRefreshToken: (): string | null =>
    _get<string>(STORAGE_KEYS.REFRESH_TOKEN),
  setRefreshToken: (token: string) => _set(STORAGE_KEYS.REFRESH_TOKEN, token),

  getUserId: (): string | null => _get<string>(STORAGE_KEYS.USER_ID),
  setUserId: (id: string) => _set(STORAGE_KEYS.USER_ID, id),

  getUdid: (): string | null => _get<string>(STORAGE_KEYS.UDID),
  setUdid: (udid: string) => _set(STORAGE_KEYS.UDID, udid),

  getTempTokens: (): { access: string | null; refresh: string | null } => ({
    access: _get<string>(STORAGE_KEYS.TEMP_ACCESS_TOKEN),
    refresh: _get<string>(STORAGE_KEYS.TEMP_REFRESH_TOKEN),
  }),

  setTempTokens: (access: string, refresh: string): void => {
    _set(STORAGE_KEYS.TEMP_ACCESS_TOKEN, access);
    _set(STORAGE_KEYS.TEMP_REFRESH_TOKEN, refresh);
  },

  clearTempTokens: (): void => {
    _remove(STORAGE_KEYS.TEMP_ACCESS_TOKEN);
    _remove(STORAGE_KEYS.TEMP_REFRESH_TOKEN);
  },

  clearSession(): void {
    const persist: StorageKey[] = [STORAGE_KEYS.UDID, STORAGE_KEYS.THEME];
    (Object.values(STORAGE_KEYS) as StorageKey[]).forEach((key) => {
      if (!persist.includes(key)) _remove(key);
    });
  },

  clearAll: (): void => localStorage.clear(),

  //User Profile
  getEmail: (): string | null => _get<string>(STORAGE_KEYS.EMAIL),
  setEmail: (email: string) => _set(STORAGE_KEYS.EMAIL, email),

  getFullName: (): string | null => _get<string>(STORAGE_KEYS.FULL_NAME),
  setFullName: (name: string) => _set(STORAGE_KEYS.FULL_NAME, name),

  getIsEmailVerified: (): boolean =>
    _get<boolean>(STORAGE_KEYS.IS_EMAIL_VERIFIED) === true,
  setIsEmailVerified: (value: boolean) =>
    _set(STORAGE_KEYS.IS_EMAIL_VERIFIED, value),

  getAccountId: (): string | null => _get<string>(STORAGE_KEYS.ACCOUNT_ID),
  setAccountId: (id: string) => _set(STORAGE_KEYS.ACCOUNT_ID, id),

  getPhoneNumber: (): string | null => _get<string>(STORAGE_KEYS.PHONE_NUMBER),
  setPhoneNumber: (num: string) => _set(STORAGE_KEYS.PHONE_NUMBER, num),

  //Feature Flags
  getIsSupportAgent: (): boolean =>
    _get<boolean>(STORAGE_KEYS.IS_SUPPORT_AGENT) === true,
  setIsSupportAgent: (value: boolean) =>
    _set(STORAGE_KEYS.IS_SUPPORT_AGENT, value),

  getIsNumberRegistered: (): boolean =>
    _get<boolean>(STORAGE_KEYS.IS_NUMBER_REG) === true,
  setIsNumberRegistered: (value: boolean) =>
    _set(STORAGE_KEYS.IS_NUMBER_REG, value),

  getIsNumberPurchased: (): boolean =>
    _get<boolean>(STORAGE_KEYS.IS_NUMBER_PURCHASED) === true,
  setIsNumberPurchased: (value: boolean) =>
    _set(STORAGE_KEYS.IS_NUMBER_PURCHASED, value),

  getAlreadyOpenedE911Once: (): boolean =>
    _get<boolean>(STORAGE_KEYS.ALREADY_OPENED_E911_ONCE) === true,
  setAlreadyOpenedE911Once: () =>
    _set(STORAGE_KEYS.ALREADY_OPENED_E911_ONCE, true),

  //Preferences
  getTheme: (): string | null => _get<string>(STORAGE_KEYS.THEME),
  setTheme: (theme: string) => _set(STORAGE_KEYS.THEME, theme),

  getDtmfMode: (): string | null => _get<string>(STORAGE_KEYS.DTMF_MODE),
  setDtmfMode: (mode: string) => _set(STORAGE_KEYS.DTMF_MODE, mode),
  removeDtmfMode: () => _remove(STORAGE_KEYS.DTMF_MODE),

  //Chat State
  getForwardedMsgIds: (): string[] =>
    _get<string[]>(STORAGE_KEYS.FORWARDED_MESSAGE_IDS) ?? [],
  setForwardedMsgIds: (ids: string[]) =>
    _set(STORAGE_KEYS.FORWARDED_MESSAGE_IDS, ids),
} as const;
